import {
  CONTRACT_CREATE_STAGES,
  CONTRACT_UPLOAD_STAGES,
  PROPOSAL_CREATE_STAGES,
  PROPOSAL_STAGES
} from "../../constants/document-stages.ts"
import type { Lead } from "../../types/lead.types.ts"

interface LeadDocumentVisibility {
  isProposalStage: boolean
  canCreateProposal: boolean
  showProposals: boolean
  canCreateContract: boolean
  canUploadContract: boolean
  showContracts: boolean
}

/**
 * The single source of truth for which document sections apply to a lead's
 * current stage — shared by the Documents tab's own visibility (hidden
 * entirely when neither section applies) and its rendered content (which
 * sections/actions show once the tab is open). Existing documents are never
 * hidden by a stage change: `showProposals`/`showContracts` fall back to
 * `.length`, so a lead moved off its creating stage keeps what it already has.
 */
function leadDocumentVisibility(lead: Lead): LeadDocumentVisibility {
  const isProposalStage = PROPOSAL_STAGES.includes(lead?.status)
  const canCreateProposal = PROPOSAL_CREATE_STAGES.includes(lead?.status)
  const showProposals = !!lead?.proposals?.length || isProposalStage

  const canCreateContract = CONTRACT_CREATE_STAGES.includes(lead?.status)
  const canUploadContract = CONTRACT_UPLOAD_STAGES.includes(lead?.status)
  const isProposalOnlyStage = canCreateProposal

  const showContracts =
    !isProposalOnlyStage &&
    (!!lead?.contracts?.length || canCreateContract || canUploadContract)

  return {
    isProposalStage,
    canCreateProposal,
    showProposals,
    canCreateContract,
    canUploadContract,
    showContracts
  }
}

/** Whether the Documents tab should appear at all for this lead. */
const leadHasDocumentsTab = (lead: Lead): boolean => {
  const { showProposals, showContracts } = leadDocumentVisibility(lead)
  return showProposals || showContracts
}

export { leadDocumentVisibility, leadHasDocumentsTab }
